import React,{useMemo,useState} from 'react';
import {FiFolder,FiPlus,FiSearch,FiGrid,FiList,FiArrowRight,FiFilm,FiLayers,FiTrash2,FiRotateCcw,FiGitMerge} from 'react-icons/fi';
import ProjectTitle from './ProjectTitle';
import MediaThumbnail from './MediaThumbnail';
import {indexProjectInterviews} from '../lib/projectWorkspace';

function ProjectCover({interviews,getUrl}) {
 const available=interviews.filter(item=>item.media_ready&&item.media_path);
 const ready=available.slice(0,4);
 if(!ready.length)return <span className="project-cover project-cover-empty" aria-hidden="true"><FiFolder/></span>;
 const extra=available.length-ready.length;
 return <span className={`project-cover project-cover-mosaic count-${ready.length}`} aria-hidden="true">
  {ready.map((item,index)=><span key={item.id} className="project-cover-cell">
   <MediaThumbnail row={item} getUrl={getUrl}/>
   {index===3&&extra>0&&<span className="project-cover-more">+{extra}</span>}
  </span>)}
 </span>;
}

// Video assets are stored once, and every project receives its own editable
// interview record. The library is separate from the list of project rows.
export default function ProjectsHome({rows,trashRows=[],mediaLibrary=[],busy,signedIn,loading,onOpen,onNew,onImportCloud,onRename,onDelete,onRestore,onPermanentDelete,onMerge,onSignIn,onResume,hasDraft,error,getUrl,onReuse}) {
 const [query,setQuery]=useState(''),[view,setView]=useState('grid'),[tab,setTab]=useState('projects');
 const [destinations,setDestinations]=useState({});
 const deletedProjects=useMemo(()=>trashRows.filter(row=>!row.data?.collectionId),[trashRows]);
 const projects=useMemo(()=>rows.filter(row=>!row.data.collectionId)
  .sort((a,b)=>new Date(b.updated_at||0)-new Date(a.updated_at||0)),[rows]);
 const interviewIndex=useMemo(()=>indexProjectInterviews(rows),[rows]);
 const search=query.trim().toLowerCase();
 const visible=useMemo(()=>projects.filter(row=>(row.data.projectName||row.data.title||'').toLowerCase().includes(search)),[projects,search]);
 const visibleTrash=useMemo(()=>deletedProjects.filter(row=>(row.data.projectName||row.data.title||'').toLowerCase().includes(search)),[deletedProjects,search]);
 const filteredMedia=useMemo(()=>mediaLibrary.filter(asset=>
   [asset.data?.displayName,asset.data?.title,asset.data?.filename].some(label=>(label||'').toLowerCase().includes(search))),[mediaLibrary,search]);
 const projectTitle=project=>project.data.projectName||project.data.title||'Untitled project';
 return <main className="projects-home">
  <div className="projects-heading">
   <div><h1>{tab==='projects'?'Your projects':tab==='trash'?'Trash':'Media Library'}</h1>
    <p>{tab==='projects'?'Find the moments. Shape the story.':tab==='trash'?'Restore projects or permanently remove them when you are certain.':'Upload once. Use your interviews across multiple projects.'}</p>
   </div>
   {tab!=='trash'&&<div className="projects-heading-actions">
    <button className="cloud-project-import-button" type="button" disabled={busy||loading||!signedIn}
     title="Import interviews from Google Drive, Dropbox or Frame.io"
     onClick={onImportCloud}><FiFilm/> Import from cloud</button>
    <button className="export-button" disabled={busy||loading} onClick={onNew}>
     <FiPlus/> {tab==='projects'?'New project':'Upload interview'}
    </button>
   </div>}
  </div>
  {error&&<div className="error-banner" role="alert">{error}</div>}
  {!signedIn&&!loading&&<div className="projects-signin"><span>Sign in to access your saved projects and interviews.</span><button className="auth-control" onClick={onSignIn}>Sign in</button></div>}
  {hasDraft&&<button className="project-resume" disabled={busy} onClick={onResume}>Continue current workspace <FiArrowRight/></button>}
  <div className="projects-content-tabs" role="tablist" aria-label="Projects and media">
   <button type="button" role="tab" aria-selected={tab==='projects'} onClick={()=>{setTab('projects');setQuery('');}}><FiFolder/> Projects <span>{projects.length}</span></button>
   <button type="button" role="tab" aria-selected={tab==='media'} onClick={()=>{setTab('media');setQuery('');}}><FiFilm/> Media Library <span>{mediaLibrary.length}</span></button>
   <button type="button" role="tab" aria-selected={tab==='trash'} onClick={()=>{setTab('trash');setQuery('');}}><FiTrash2/> Trash <span>{deletedProjects.length}</span></button>
  </div>
  <div className="projects-tools">
   <label><FiSearch/><input aria-label={tab==='projects'?'Search projects':tab==='trash'?'Search Trash':'Search Media Library'}
      placeholder={tab==='projects'?'Search projects…':tab==='trash'?'Search deleted projects…':'Search your videos…'} value={query} onChange={event=>setQuery(event.target.value)}/></label>
   {tab==='projects'&&<div role="group" aria-label="Project layout">
    <button aria-label="Grid view" aria-pressed={view==='grid'} onClick={()=>setView('grid')}><FiGrid/></button>
    <button aria-label="List view" aria-pressed={view==='list'} onClick={()=>setView('list')}><FiList/></button>
   </div>}
  </div>
  {busy&&<p role="status" className="projects-progress">Updating your library…</p>}
  {tab==='projects' ? <>
   {loading||(busy&&!projects.length)?<p role="status">Loading your projects…</p>:
    <div className={`projects-collection projects-${view}`}>
     {visible.map(row=>{const interviews=interviewIndex.get(row.id)||[],count=interviews.length,date=new Date(row.updated_at);
      return <article className="project-tile" key={row.id}>
       <button className="project-tile-open" disabled={busy} onClick={()=>onOpen(row.id)} aria-label={`Open ${projectTitle(row)}`}>
        <ProjectCover interviews={interviews} getUrl={getUrl}/>
        <span className="project-tile-name">{projectTitle(row)}</span>
        <span className="project-tile-meta">{count} interview{count===1?'':'s'}{!Number.isNaN(date.getTime())?` · ${date.toLocaleDateString(undefined,{month:'short',day:'numeric'})}`:''}</span>
       </button>
       <details className="project-tile-menu"><summary aria-label={`Options for ${projectTitle(row)}`}>⋯</summary>
        <div><small className="project-menu-label">Rename project</small><ProjectTitle title={projectTitle(row)} disabled={busy} onSave={name=>onRename(row.id,name)}/>
         <button className="project-merge-button" disabled={busy||projects.length<2} onClick={()=>onMerge(row)}><FiGitMerge/> Merge into another project</button>
         <button className="project-delete" disabled={busy} onClick={()=>onDelete(row)}><FiTrash2/> Move to Trash</button>
        </div>
       </details>
      </article>;
     })}
    </div>}
   {!loading&&!busy&&!visible.length&&<div className="projects-empty"><FiFolder/><h2>{query?'No matching projects':'Your next story starts here'}</h2>
    <p>{query?'Try another project name.':'Create a project, then add your interviews.'}</p></div>}
  </> : tab==='trash' ? <>
   <div className="library-explainer"><FiTrash2 aria-hidden="true"/>
    <p>Projects here are recoverable. Their interviews, Assembly, named versions and feedback remain intact. Files remain in Media Library and storage is not reclaimed until you choose permanent deletion.</p>
   </div>
   {loading?<p role="status">Loading Trash…</p>:
    <div className="projects-collection projects-grid">
     {visibleTrash.map(row=><article className="project-tile project-trash-tile" key={row.id}>
      <div className="project-trash-cover" aria-hidden="true"><FiFolder/></div>
      <strong className="project-tile-name">{projectTitle(row)}</strong>
      <small className="project-tile-meta">Moved to Trash {new Date(row.deleted_at).toLocaleDateString(undefined,{day:'numeric',month:'short',year:'numeric'})}</small>
      <div className="project-trash-actions">
       <button type="button" disabled={busy} className="project-trash-restore" onClick={()=>onRestore(row)}><FiRotateCcw/> Restore project</button>
       <button type="button" disabled={busy} className="project-trash-permanent" onClick={()=>onPermanentDelete(row)}><FiTrash2/> Delete permanently</button>
      </div>
     </article>)}
    </div>}
   {!loading&&!visibleTrash.length&&<div className="projects-empty"><FiTrash2/><h2>{query?'No matching deleted projects':'Trash is empty'}</h2>
    <p>{query?'Try another project name.':'Deleted projects appear here until you restore or permanently delete them.'}</p></div>}
  </> : <>
   <div className="library-explainer"><FiLayers aria-hidden="true"/>
    <p>Each uploaded interview is saved once. Add it to a new or existing project without uploading or transcribing it again. Each project keeps its own edits and Assembly.</p>
   </div>
   {loading?<p role="status">Loading saved media…</p>:
    <div className="media-library-grid">
     {filteredMedia.map(asset=>{
      const projectsUsing=projects.filter(project=>(interviewIndex.get(project.id)||[]).some(item=>item.media_path===asset.media_path));
      const usedIds=new Set(projectsUsing.map(project=>project.id));
      const destination=destinations[asset.id]||'new';
      const filename=asset.data?.filename||asset.data?.title||'Untitled video';
      const duration=Number(asset.data?.duration)||0;
      return <article className="media-library-card" key={asset.id}>
       <div className="media-library-preview"><MediaThumbnail row={{...asset,media_ready:true}} getUrl={getUrl}/></div>
       <div className="media-library-info">
        <strong title={filename}>{filename}</strong>
        <small>{Math.round((asset.media_bytes||0)/1048576*10)/10} MB{duration?` · ${Math.floor(duration/60)}m ${Math.floor(duration%60)}s`:''}{asset.data?.words?.length?` · ${asset.data.words.length.toLocaleString()} words`:''}</small>
        <span>{projectsUsing.length} project{projectsUsing.length===1?'':'s'} using this file</span>
       </div>
       <div className="media-library-attach">
        <label htmlFor={`reuse-destination-${asset.id}`}>Use video in</label>
        <select id={`reuse-destination-${asset.id}`} value={destination} onChange={event=>setDestinations(old=>({...old,[asset.id]:event.target.value}))} disabled={busy||!signedIn}>
         <option value="new">New project</option>
         {projects.map(project=><option key={project.id} value={project.id} disabled={usedIds.has(project.id)}>
          {projectTitle(project)}{usedIds.has(project.id)?' · already added':''}
         </option>)}
        </select>
        <button type="button" className="media-library-use" disabled={busy||!signedIn}
          onClick={()=>onReuse(asset.id,destination==='new'?null:destination)}>
          <FiPlus/> {destination==='new'?'Create from video':'Add to project'}
        </button>
       </div>
      </article>;
     })}
    </div>}
   {!loading&&!filteredMedia.length&&<div className="projects-empty"><FiFilm/><h2>{query?'No matching videos':'Your Media Library is empty'}</h2>
    <p>{query?'Try another filename.':'Add an interview to a project and it will appear here automatically for reuse.'}</p></div>}
  </>}
 </main>;
}
